import { useEffect, useState } from "react";
import { Row, Col, message, Space } from 'antd';
import { EditOutlined } from "@ant-design/icons";
import { getLangList } from "@/lib/i18n";
import SaleConfig from "@/config/sale";
import { ReButton, ReModal } from '@/components/refineElement';
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import { hasPermission } from "@/lib/permissionUtil";
import DraggableTabs from "@/components/draggableTabs";
import Product from './Product';
import { CategoryType, SaleSubjectType } from './types';
import ModalSaleSubject from './ModalSaleSubject'

const { createSaleSubjectInfo, updateSaleSubjectInfo, deleteSaleSubjectInfo } = SaleConfig;

interface Props {
  categoryArray: CategoryType[];
  selectedGroupId: string;
  selectedGroupName: string;
  saleSubjectArray: SaleSubjectType[];
  getAllSaleSubjectInfoFunction: Function;
}

function SaleSubject(props: Props) {
  const { categoryArray, selectedGroupId, selectedGroupName, saleSubjectArray, getAllSaleSubjectInfoFunction } = props;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const [selectedSaleSubjectId, setSelectedSaleSubjectId] = useState('');
  const [saleSubjectInfo, setSaleSubjectInfo] = useState({} as SaleSubjectType);
  const saleT: any = getLangList("sale");
  const errorT: any = getLangList("error");
  const okFunction = {
    create: createSaleSubjectInfoFunction,
    update: updateSaleSubjectInfoFunction,
  };

  useEffect(() => {
    setSelectedSaleSubjectId(saleSubjectArray?.[0]?.saleSubjectId?.toString() ?? '');
  }, []);

  const toggleModal = (handleType: string = '') => {
    setIsModalVisible(!isModalVisible);
    setHandleType(handleType);
  };

  const clickCreate = () => {
    setSaleSubjectInfo({} as SaleSubjectType);
    toggleModal('create');
  }

  const clickEdit = (data: SaleSubjectType) => (e: any) => {
    e.stopPropagation();
    setSaleSubjectInfo(data);
    toggleModal('update');
  }

  const clickDelete = () => {
    showDelConfirm(() => deleteSaleSubjectInfoFunction(selectedSaleSubjectId));
  };

  function createSaleSubjectInfoFunction(values: SaleSubjectType) {
    const { saleSubjectName, saleGroupId } = values;
    const params = { saleSubjectName, saleGroupId };

    createSaleSubjectInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getAllSaleSubjectInfoFunction();
    });
  }

  function updateSaleSubjectInfoFunction(values: SaleSubjectType) {
    const { saleSubjectId, sort } = saleSubjectInfo;
    const { saleSubjectName, saleGroupId } = values;
    const updateData = {
      saleSubjectId,
      saleSubjectName,
      saleGroupId,
      sort,
    };
    const params = saleSubjectArray.map((item: SaleSubjectType) => {
      if (item.saleSubjectId === saleSubjectId) {
        return updateData;
      } else {
        return item;
      }
    });

    if (!hasPermission('SALE_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateSaleSubjectInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getAllSaleSubjectInfoFunction();
      setSelectedSaleSubjectId(saleSubjectArray?.[0]?.saleSubjectId?.toString() ?? '');
    });
  }

  function updateSaleSubjectSortFunction(params: SaleSubjectType[]) {
    updateSaleSubjectInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getAllSaleSubjectInfoFunction();
      setSelectedSaleSubjectId(saleSubjectArray?.[0]?.saleSubjectId?.toString() ?? '');
    });
  }

  const deleteSaleSubjectInfoFunction = (id: string) => {
    const params = { saleSubjectId: id };

    deleteSaleSubjectInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getAllSaleSubjectInfoFunction();
      setSelectedSaleSubjectId(saleSubjectArray?.[0]?.saleSubjectId?.toString() ?? '');
    });
  }

  return (
    <>
      <Row className="sale-page">
        <Col span={24}>
          <DraggableTabs
            type="card"
            onChange={setSelectedSaleSubjectId}
            activeKey={selectedSaleSubjectId}
            destroyInactiveTabPane
            items={(
              saleSubjectArray.map((item: SaleSubjectType) => {
                const { saleSubjectName, saleSubjectId } = item;

                return {
                  key: `${saleSubjectId}`,
                  label: (
                    <Space>
                      <span>{saleSubjectName}</span>
                      {selectedSaleSubjectId === saleSubjectId.toString() && hasPermission(['SALE_EDIT', 'SALE_DELETE']) &&
                        <span onClick={clickEdit(item)}><EditOutlined /></span>
                      }
                    </Space>
                  ),
                  children: (
                    <Product
                      selectedGroupName={selectedGroupName}
                      selectedSaleSubjectId={selectedSaleSubjectId}
                      saleSubjectArray={saleSubjectArray}
                    />
                  ),
                  apiParams: item,
                }
              })
            )}
            tabBarExtraContent={hasPermission('SALE_NEW') &&
              <ReButton iconName='plus' onClick={clickCreate}>
                {saleT.NEW_SUBJECT}
              </ReButton>
            }
            params={{
              sortParamName: 'sort',
              updateSortFunction: updateSaleSubjectSortFunction,
              hasUpdatePermission: hasPermission('SALE_EDIT'),
            }}
          />
        </Col>
      </Row>

      {isModalVisible &&
        <ReModal
          title={saleT.SUBJECT}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
          CollectedForm={ModalSaleSubject}
          formProps={{
            handleType,
            info: saleSubjectInfo,
            clickDelete,
            categoryArray,
            selectedGroupId,
          }}
        />
      }
    </>
  )
};

export default SaleSubject;